import { memo, useEffect, useState } from "react";
import type { CctvCamera } from "@kmitl/shared";
import { useFeed } from "../hooks/useFeed";

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";
const IMG_REFRESH_MS = 8_000; // refresh tile snapshots every 8 s

/**
 * Tiny "live" indicator. Animates the dot without a heavy CSS keyframe.
 */
function LiveDot() {
  return <span className="cctv-strip__live-dot" aria-hidden />;
}

/**
 * One CCTV tile: snapshot (auto-refreshed) with overlay name + distance.
 * Click opens the parent-supplied modal.
 */
const CctvTile = memo(function CctvTile({
  camera,
  onClick,
}: {
  camera: CctvCamera;
  onClick: (c: CctvCamera) => void;
}) {
  // Cache-bust the snapshot every refresh tick so the browser actually
  // re-fetches the JPG (Longdo's snapshots rotate with the same URL).
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), IMG_REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  // Approximate bearing from KMITL center to the camera so the operator
  // can read direction at a glance (N / NE / E / SE / S / SW / W / NW).
  const KMITL_LAT = 13.729;
  const KMITL_LNG = 100.779;
  const dy = camera.lat - KMITL_LAT;
  const dx = camera.lng - KMITL_LNG;
  const bearing = (Math.atan2(dx, dy) * 180) / Math.PI;
  const cardinal = (() => {
    const b = (bearing + 360) % 360;
    if (b < 22.5 || b >= 337.5) return "N";
    if (b < 67.5) return "NE";
    if (b < 112.5) return "E";
    if (b < 157.5) return "SE";
    if (b < 202.5) return "S";
    if (b < 247.5) return "SW";
    if (b < 292.5) return "W";
    return "NW";
  })();

  const distKm = Math.hypot(dy * 111, dx * 111);

  return (
    <button
      type="button"
      className="cctv-strip__tile"
      onClick={() => onClick(camera)}
      title={`${camera.name} · ${distKm.toFixed(1)} km ${cardinal} · open stream`}
    >
      <div className="cctv-strip__frame">
        {camera.imageUrl ? (
          <img
            src={`${camera.imageUrl}${camera.imageUrl.includes("?") ? "&" : "?"}_t=${tick}`}
            alt={camera.name}
            className="cctv-strip__img"
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="cctv-strip__placeholder mono">NO SIGNAL</div>
        )}
        <div className="cctv-strip__live">
          <LiveDot />
          <span>REC</span>
        </div>
      </div>
      <div className="cctv-strip__meta">
        <span className="cctv-strip__name" title={camera.name}>
          {camera.name.replace(/^\([^)]+\)\s*/, "").trim() || camera.name}
        </span>
        <span className="cctv-strip__dist mono">
          {cardinal} · {distKm.toFixed(1)} km
        </span>
      </div>
    </button>
  );
});

interface Props {
  onOpenCamera: (camera: CctvCamera) => void;
  /** Optional cap on the number of tiles shown. Defaults to 6. */
  maxTiles?: number;
  /** Refresh interval for the camera catalog. Defaults to 5 min. */
  refreshMs?: number;
  /** When false, returns null — used as a drawer that the topbar toggles. */
  open?: boolean;
}

/**
 * Lopburi-style horizontal CCTV strip at the top of the dashboard.
 * A row of live-snapshot tiles, one per camera in the regional bbox
 * (KMITL outer + the eastern Lat-Krabang arterial network), each
 * clickable to open the full stream modal.
 *
 * `open={false}` returns null — the operator toggles it from a CCTV
 * chip in the topbar, so the dashboard's first read is the map, not a
 * 6-tile camera strip.
 */
export const CctvStrip = memo(function CctvStrip({
  onOpenCamera,
  maxTiles = 6,
  refreshMs = 5 * 60_000,
  open = true,
}: Props) {
  const feed = useFeed<CctvCamera>(`${API_BASE}/api/cctv/longdo`, refreshMs);
  const cameras = feed.data.slice(0, maxTiles);

  if (!open) return null;
  if (feed.fallbackTier === "loading" && feed.data.length === 0) return null;
  if (cameras.length === 0) return null;

  return (
    <section className="cctv-strip" aria-label="Live CCTV cameras near campus">
      <div className="cctv-strip__head">
        <span className="mono eyebrow cctv-strip__tag">CCTV · LIVE</span>
        <span className="mono cctv-strip__count">
          {feed.data.length} {feed.data.length === 1 ? "camera" : "cameras"}
        </span>
        <span className="mono cctv-strip__source">longdo · regional approach roads</span>
      </div>
      <div className="cctv-strip__row" role="list">
        {cameras.map((cam) => (
          <CctvTile key={cam.id} camera={cam} onClick={onOpenCamera} />
        ))}
      </div>
    </section>
  );
});
